import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { Badge } from '@components/ui/badge';
import type { GenerationListItem } from '@stratumn/studio-shared';
import { Link, type LinkProps } from 'react-router-dom';
import { nameToColor } from '@/lib/name-to-color';
import { cn } from '@/lib/utils';
import {
  formatDurationLong,
  formatElapsedTime,
  generationStatusToProgress
} from '@/pages/Settings/generation';
import { VersionBadge } from '@/pages/Workspaces/components/VersionBadge';
import { ENV, ENV_TO_READABLE_NAME } from '@/utils/constants';

type GenerationRowProps = {
  generation: GenerationListItem;
  to: LinkProps['to'];
  workspaceColor?: string;
  isFirst?: boolean;
  isLast?: boolean;
};

export const GenerationRow = ({
  generation,
  to,
  workspaceColor,
  isFirst,
  isLast
}: GenerationRowProps) => (
  <Link
    className={cn(
      'block border border-black/[0.08] border-b-0 bg-white p-4 text-sm text-zinc-500 hover:bg-gray-50',
      isFirst && 'rounded-t-md',
      isLast && 'rounded-b-md border-b'
    )}
    to={to}
  >
    <div className="grid w-full grid-cols-[8rem_10rem_9rem_1fr] items-center gap-4">
      <div>
        <div className="font-medium text-zinc-950">{generation.id}</div>
        <div className="flex flex-row items-center">
          {ENV_TO_READABLE_NAME[ENV]}
          {generation.isCurrentlyDeployed && (
            <Badge className="ml-2 font-normal" variant="pastelBlue">
              Current
            </Badge>
          )}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1.5">
          {generation.workspaceName ? (
            <>
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: workspaceColor || '#6366f1' }}
              />
              <span className="truncate font-medium text-zinc-950">
                {generation.workspaceName}
              </span>
            </>
          ) : (
            <span className="text-zinc-400">No workspace</span>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-1.5">
          <VersionBadge version={generation.workspaceVersion} />
        </div>
      </div>
      <div>
        <div className="flex flex-row items-center">
          {generationStatusToProgress(generation.status)}
          <div className="ml-2.5 font-medium">{generation.status}</div>
        </div>
        <div className="whitespace-nowrap pl-6">
          took {formatDurationLong(generation.duration)},{' '}
          {formatElapsedTime(generation.createdAt)}
        </div>
      </div>
      <div className="justify-self-end">
        <div className="flex flex-row items-center">
          {generation.createdBy.name}
          <Avatar className="ml-2 h-6 w-6">
            <AvatarImage src={generation.createdBy.avatar ?? undefined} />
            <AvatarFallback
              className="font-medium text-white text-xs"
              style={{
                backgroundColor: nameToColor(generation.createdBy.name)
              }}
            >
              {generation.createdBy.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>
    </div>
  </Link>
);

export const GenerationList = ({
  className,
  children
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={cn('w-full overflow-hidden rounded-md shadow-sm', className)}>
    {children}
  </div>
);
